import {
  login,
  setAuthToken,
  createBeer,
  generateLabelImage,
  beerPageUrl,
  beerSlug,
  fetchBeers,
} from './api';
import { LabelFields, renderPreview, exportPng } from './label';
import { renderImagePicker } from './images';
import { renderRatingInputs } from './ratings';

let adminMode = false;
let clickCount = 0;
let clickTimer: ReturnType<typeof setTimeout> | null = null;

const AUTH_COOKIE = 'koz_admin';
const COOKIE_DAYS = 7;

// Sent to the image model verbatim. The artwork lands in a tall portrait slot
// that the browser slice-crops, hence the composition wording — edit the subject
// (or anything else) right in the field.
const ARTWORK_PROMPT =
  'Beer label artwork: a stuntman leaping from an explosion into a blue sky. ' +
  'Vertical portrait composition, subject centered and filling the frame edge ' +
  'to edge, bold graphic illustration style, high contrast, no text, ' +
  'no lettering, no border, no bottle mockup.';

function setCookie(name: string, value: string, days: number): void {
  const expires = new Date(Date.now() + days * 86400000).toUTCString();
  document.cookie = `${name}=${encodeURIComponent(value)};expires=${expires};path=/;SameSite=Lax`;
}

function getCookie(name: string): string | null {
  const hit = document.cookie.split('; ').find((c) => c.startsWith(name + '='));
  return hit ? decodeURIComponent(hit.split('=').slice(1).join('=')) : null;
}

function delCookie(name: string): void {
  document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`;
}

// Shared by fresh login and cookie restore.
function enterAdmin(token: string, onUpdate: () => void): void {
  setAuthToken(token);
  adminMode = true;
  document.body.classList.add('admin-active');
  onUpdate();
  showAddBeerButton(onUpdate);
}

export function isAdminMode(): boolean {
  return adminMode;
}

export function initAdmin(onUpdate: () => void): void {
  const logo = document.getElementById('logo');
  if (!logo) return;

  // Restore an existing admin session from the cookie. If the token is stale
  // (creds changed), authed actions will 401 and the user can re-login.
  const saved = getCookie(AUTH_COOKIE);
  if (saved) enterAdmin(saved, onUpdate);

  logo.addEventListener('click', () => {
    clickCount++;
    if (clickTimer) clearTimeout(clickTimer);
    clickTimer = setTimeout(() => {
      clickCount = 0;
    }, 3000);

    if (clickCount >= 5) {
      clickCount = 0;
      if (clickTimer) clearTimeout(clickTimer);
      if (adminMode) {
        exitAdmin();
      } else {
        showPasswordPanel(onUpdate);
      }
    }
  });
}

function showPasswordPanel(onUpdate: () => void): void {
  const existing = document.getElementById('admin-overlay');
  if (existing) existing.remove();

  const overlay = document.createElement('div');
  overlay.id = 'admin-overlay';
  overlay.className = 'overlay';
  overlay.innerHTML = `
    <div class="overlay-content admin-panel">
      <button class="overlay-close">&times;</button>
      <h2>Admin Login</h2>
      <form id="admin-login-form">
        <div class="form-group">
          <label>Username</label>
          <input type="text" id="admin-username" required autocomplete="username">
        </div>
        <div class="form-group">
          <label>Password</label>
          <input type="password" id="admin-password" required autocomplete="current-password">
        </div>
        <p id="admin-error" class="error-text" style="display:none"></p>
        <button type="submit" class="btn btn-login">Login</button>
      </form>
    </div>
  `;

  document.body.appendChild(overlay);
  requestAnimationFrame(() => overlay.classList.add('active'));

  overlay.querySelector('.overlay-close')!.addEventListener('click', () => closeOverlay(overlay));
  closeOnBackdrop(overlay);

  const form = document.getElementById('admin-login-form') as HTMLFormElement;
  form.addEventListener('submit', async (e) => {
    e.preventDefault();
    const username = (document.getElementById('admin-username') as HTMLInputElement).value;
    const password = (document.getElementById('admin-password') as HTMLInputElement).value;
    const errorEl = document.getElementById('admin-error')!;

    try {
      const token = await login(username, password);
      setCookie(AUTH_COOKIE, token, COOKIE_DAYS);
      enterAdmin(token, onUpdate);
      overlay.classList.remove('active');
      setTimeout(() => overlay.remove(), 300);
    } catch (err) {
      errorEl.textContent = (err as Error).message || 'Invalid credentials';
      errorEl.style.display = 'block';
    }
  });
}

function exitAdmin(): void {
  adminMode = false;
  setAuthToken(null);
  delCookie(AUTH_COOKIE);
  document.body.classList.remove('admin-active');
  document.getElementById('add-beer-btn')?.remove();
  document.getElementById('create-label-btn')?.remove();
}

function showAddBeerButton(onUpdate: () => void): void {
  const existing = document.getElementById('add-beer-btn');
  if (existing) return;

  const beerSection = document.getElementById('beer');
  if (!beerSection) return;

  const btn = document.createElement('button');
  btn.id = 'add-beer-btn';
  btn.className = 'btn btn-add-beer admin-only';
  btn.textContent = '+ Add New Drink';
  btn.addEventListener('click', () => showAddBeerForm(onUpdate));

  const labelBtn = document.createElement('button');
  labelBtn.id = 'create-label-btn';
  labelBtn.className = 'btn btn-add-beer admin-only';
  labelBtn.textContent = '🏷 Create Label';
  labelBtn.addEventListener('click', () => showLabelDesigner());

  const heading = beerSection.querySelector('h2');
  if (heading) {
    heading.after(labelBtn);
    heading.after(btn);
  } else {
    beerSection.prepend(labelBtn);
    beerSection.prepend(btn);
  }
}

export function closeOverlay(overlay: HTMLElement): void {
  overlay.classList.remove('active');
  setTimeout(() => overlay.remove(), 300);
}

// Backdrop click closes the panel — but only when the press *started* on the
// backdrop. Otherwise a text selection dragged out of an input ends its click
// on the backdrop and wipes the form.
export function closeOnBackdrop(overlay: HTMLElement): void {
  let pressedBackdrop = false;
  overlay.addEventListener('mousedown', (e) => {
    pressedBackdrop = e.target === overlay;
  });
  overlay.addEventListener('click', (e) => {
    if (pressedBackdrop && e.target === overlay) closeOverlay(overlay);
  });
}

function showLabelDesigner(): void {
  document.getElementById('label-overlay')?.remove();

  const overlay = document.createElement('div');
  overlay.id = 'label-overlay';
  overlay.className = 'overlay';
  overlay.innerHTML = `
    <div class="overlay-content label-designer">
      <button class="overlay-close">&times;</button>
      <h2>Create Label</h2>
      <div class="label-designer-body">
        <form class="label-form">
          <div class="form-group">
            <label>Name (name)</label>
            <input type="text" name="name" value="КАСКАДЕР">
          </div>
          <div class="form-row">
            <div class="form-group">
              <label>Style</label>
              <input type="text" name="style" value="IPA">
            </div>
            <div class="form-group">
              <label>ABV</label>
              <input type="text" name="alk" value="6.5%">
            </div>
            <div class="form-group">
              <label>IBU</label>
              <input type="text" name="ibu" value="45">
            </div>
            <div class="form-group">
              <label>OG</label>
              <input type="text" name="og" value="1.056">
            </div>
          </div>
          <div class="form-group">
            <label>Bottling date</label>
            <input type="date" name="date" value="${new Date().toLocaleDateString('sv-SE')}">
          </div>
          <div class="form-group">
            <label>Ingredients (contains)</label>
            <textarea name="contains" rows="2">вода, солод, хмель, дрожжи</textarea>
          </div>
          <div class="form-group">
            <label>QR link</label>
            <p class="label-qr-url"></p>
          </div>
          <div class="form-group">
            <label>Accent color</label>
            <select name="accent">
              <option value="black">Black</option>
              <option value="white">White</option>
            </select>
          </div>
          <div class="form-group">
            <label>Artwork prompt</label>
            <textarea name="artwork" rows="6">${ARTWORK_PROMPT}</textarea>
            <button type="button" class="btn btn-generate">Generate artwork</button>
            <p class="label-status" style="display:none"></p>
          </div>
          <div class="form-group">
            <label>…or upload your own artwork</label>
            <input type="file" class="label-upload" accept="image/*">
          </div>
          <div class="label-actions">
            <button type="button" class="btn btn-save btn-export">Save as PNG</button>
          </div>
        </form>
        <div class="label-preview"><p class="label-hint">Preview loading…</p></div>
      </div>
    </div>
  `;

  document.body.appendChild(overlay);
  requestAnimationFrame(() => overlay.classList.add('active'));

  overlay.querySelector('.overlay-close')!.addEventListener('click', () => closeOverlay(overlay));
  closeOnBackdrop(overlay);

  const form = overlay.querySelector('.label-form') as HTMLFormElement;
  const preview = overlay.querySelector('.label-preview') as HTMLElement;
  const status = overlay.querySelector('.label-status') as HTMLElement;
  let artwork: string | null = null;

  const qrUrlEl = overlay.querySelector('.label-qr-url') as HTMLElement;
  // A printed QR is permanent, so warn before it bakes in a dead link: a dev
  // origin, or a name that matches no beer on the site.
  let names: string[] = [];
  fetchBeers()
    .then((bs) => {
      names = bs.map((b) => b.name);
      refresh();
    })
    .catch(() => {});

  const qrWarning = (fields: LabelFields): string => {
    if (!fields.url) return 'Enter a name — the QR needs it';
    if (location.hostname === 'localhost' || location.hostname === '127.0.0.1')
      return 'Local address: this QR will be dead in print. Design the label on the live site.';
    if (names.length && !names.some((n) => beerSlug(n) === beerSlug(fields.name)))
      return `No beer named “${fields.name}” on the site — this QR will land on the list.`;
    return '';
  };

  const readFields = (): LabelFields => {
    const data = new FormData(form);
    const get = (k: string) => (data.get(k) as string) || '';
    const iso = get('date'); // yyyy-mm-dd from the native date input
    return {
      name: get('name'),
      style: get('style'),
      alk: get('alk'),
      ibu: get('ibu'),
      og: get('og'),
      date: iso ? iso.split('-').reverse().join('.') : '',
      contains: get('contains'),
      url: get('name').trim() ? beerPageUrl(get('name')) : '',
      accent: get('accent') === 'white' ? 'white' : 'black',
      artwork,
    };
  };

  const refresh = () => {
    const fields = readFields();
    const warning = qrWarning(fields);
    qrUrlEl.textContent = warning ? `${fields.url} — ${warning}`.trim() : fields.url;
    qrUrlEl.classList.toggle('label-qr-bad', !!warning);
    renderPreview(preview, fields).catch((err) => {
      preview.innerHTML = `<p class="error-text">${err.message}</p>`;
    });
  };

  form.addEventListener('input', refresh);
  refresh();

  overlay.querySelector('.btn-generate')!.addEventListener('click', async () => {
    const desc = (form.elements.namedItem('artwork') as HTMLTextAreaElement).value.trim();
    if (!desc) {
      status.textContent = 'Enter a description first';
      status.style.display = 'block';
      return;
    }
    const genBtn = overlay.querySelector('.btn-generate') as HTMLButtonElement;
    genBtn.disabled = true;
    status.textContent = 'Generating… (this can take ~20s)';
    status.style.display = 'block';
    try {
      artwork = await generateLabelImage(desc);
      status.textContent = 'Done';
      refresh();
    } catch (err) {
      status.textContent = (err as Error).message;
    } finally {
      genBtn.disabled = false;
    }
  });

  // Upload takes the same slot as a generated image: the label embeds the
  // artwork as a data URI either way, so nothing has to reach the server.
  overlay.querySelector('.label-upload')!.addEventListener('change', (e) => {
    const file = (e.target as HTMLInputElement).files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      artwork = reader.result as string;
      status.textContent = `Using ${file.name}`;
      status.style.display = 'block';
      refresh();
    };
    reader.readAsDataURL(file);
  });

  overlay.querySelector('.btn-export')!.addEventListener('click', async () => {
    const btn = overlay.querySelector('.btn-export') as HTMLButtonElement;
    btn.disabled = true;
    try {
      const f = readFields();
      await exportPng(f, `${f.name || 'label'}.png`);
    } catch (err) {
      status.textContent = (err as Error).message;
      status.style.display = 'block';
    } finally {
      btn.disabled = false;
    }
  });
}

function showAddBeerForm(onUpdate: () => void): void {
  const existing = document.getElementById('beer-overlay');
  if (existing) existing.remove();

  const overlay = document.createElement('div');
  overlay.id = 'beer-overlay';
  overlay.className = 'overlay';
  overlay.innerHTML = `
    <div class="overlay-content beer-detail">
      <button class="overlay-close">&times;</button>
      <h2 class="beer-detail-name">Add New Drink</h2>
      <form class="beer-add-form">
        <div class="form-group">
          <label>Name</label>
          <input type="text" name="name" required>
        </div>
        <div class="form-group">
          <label>Style (optional)</label>
          <input type="text" name="style" placeholder="e.g. IPA, melomel, herbal infusion">
        </div>
        <div class="form-row">
          <div class="form-group">
            <label>ABV (%)</label>
            <input type="number" name="abv" step="0.1" required>
          </div>
          <div class="form-group">
            <label>OG (opt.)</label>
            <input type="text" name="og">
          </div>
          <div class="form-group">
            <label>IBU (opt.)</label>
            <input type="text" name="ibu">
          </div>
        </div>
        <div class="form-row">
          <div class="form-group">
            <label>Bottling Date</label>
            <input type="date" name="bottling_date">
          </div>
          <div class="form-group">
            <label>Bottles in stock</label>
            <input type="number" name="stock" min="0" value="0">
          </div>
        </div>
        <div class="form-group">
          <label>Description</label>
          <textarea name="description" rows="4"></textarea>
        </div>
        <div class="form-group">
          <label>Tasting</label>
          ${renderRatingInputs()}
          <textarea name="tasting_notes" rows="3" placeholder="Impressions"></textarea>
        </div>
        <div class="form-group">
          <label>Images</label>
          <div class="image-picker"></div>
        </div>
        <button type="submit" class="btn btn-save">Add Beer</button>
      </form>
    </div>
  `;

  document.body.appendChild(overlay);
  requestAnimationFrame(() => overlay.classList.add('active'));

  overlay.querySelector('.overlay-close')!.addEventListener('click', () => closeOverlay(overlay));
  closeOnBackdrop(overlay);

  const form = overlay.querySelector('.beer-add-form') as HTMLFormElement;
  const picker = renderImagePicker(overlay.querySelector('.image-picker') as HTMLElement);
  form.addEventListener('submit', async (e) => {
    e.preventDefault();
    const formData = new FormData(form);
    picker.appendTo(formData);
    try {
      await createBeer(formData);
      overlay.classList.remove('active');
      setTimeout(() => overlay.remove(), 300);
      onUpdate();
    } catch (err) {
      alert('Failed to add the drink');
    }
  });
}
